import { Controller, useFormContext, useWatch } from "react-hook-form";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./CollectionSettings.scss";
import { TextInput, CheckBox } from "../../../../../design-system/controls";
import { Tooltip } from "../../../../../design-system/components";
import { REQUIRED_ERROR_MESSAGE } from "../../../../../utils";

const DEFAULT_MAX_DAYS_NOT_REPORTED_TO_CLOSE_FINDING = 30;
const DEFAULT_MAX_DAYS_NOT_COLLECTED_TO_CLOSE_FINDING = 7;
const MAX_DAYS_TO_CLOSE_FINDING = 365;

interface PeriodicSettingsProps {
  disabled?: boolean;
  shouldEnableLastCollected?: boolean;
  shouldEnableLastReported?: boolean;
  maxDaysNotSeenToCloseSetting?: number;
  lastCollectedThreshold?: number;
  shouldCheckLastCollected?: boolean;
  shouldCheckLastReported?: boolean;
}

const PeriodicSettings: React.FC<PeriodicSettingsProps> = ({
  disabled,
  shouldEnableLastCollected,
  shouldEnableLastReported,
  maxDaysNotSeenToCloseSetting,
  lastCollectedThreshold,
  shouldCheckLastCollected,
  shouldCheckLastReported,
}) => {
  const {
    control,
    formState: { errors },
    trigger,
  } = useFormContext();

  const { t } = useTranslation();
  const enableLastCollectedThreshold = useWatch({
    control,
    name: `enableLastCollectedThreshold`,
    defaultValue: !!shouldCheckLastCollected,
  });

  const enableLastReportedThreshold = useWatch({
    control,
    name: `enableLastReportedThreshold`,
    defaultValue: !!shouldCheckLastReported,
  });

  const lastCollectedThresholdFv = useWatch({
    name: "lastCollectedThreshold",
    defaultValue: !!lastCollectedThreshold
      ? lastCollectedThreshold
      : DEFAULT_MAX_DAYS_NOT_COLLECTED_TO_CLOSE_FINDING,
  });

  const maxNotSeenToClose = useWatch({
    name: "maxNotSeenToClose",
    defaultValue: !!maxDaysNotSeenToCloseSetting
      ? maxDaysNotSeenToCloseSetting
      : DEFAULT_MAX_DAYS_NOT_REPORTED_TO_CLOSE_FINDING,
  });

  useEffect(() => {
    trigger(["maxNotSeenToClose", "lastCollectedThreshold"]);
  }, [
    enableLastCollectedThreshold,
    enableLastReportedThreshold,
    lastCollectedThresholdFv,
    maxNotSeenToClose,
    trigger,
  ]);

  return (
    <div className="periodic-settings">
      <h4 className={"margin-top-20"}>{t("Automatic Resolution Settings")}</h4>
      <p>
        Seemplicity uses two terms to relate to when we collect data from data
        sources and when the third party product records a finding:
      </p>
      <ul className={"show-bullets"}>
        <li>
          <span className="semibold">{t("Last Collected Time")}</span>{" "}
          {t(`is the
                most recent time that Seemplicity collected the finding, even if
                there is no change`)}
        </li>
        <li>
          <span className="semibold">{t("Last Reported Time")}</span>{" "}
          {t(`is the most
                recent time the data source claimed it scanned the resource and
                identified the finding`)}
        </li>
      </ul>
      <div className="last-collected-fields-wrap">
        <div className="last-collected-threshold-toggle-and-value checkbox-and-number">
          <Controller
            name={`enableLastReportedThreshold`}
            defaultValue={enableLastReportedThreshold}
            render={({ field: { value, onChange } }) => {
              return (
                <CheckBox
                  className="last-reported-threshold-checkbox"
                  data-testid={"enable-last-reported-threshold"}
                  onChange={(val, newValue) => {
                    onChange(newValue);
                  }}
                  disabled={!shouldEnableLastReported || disabled}
                  checked={value}
                  label={
                    <span>
                      {t("Automatically close any findings where the")}{" "}
                      <span className="semibold">
                        {t("Last Reported Time")}
                      </span>{" "}
                      {t("is more than")}
                    </span>
                  }
                />
              );
            }}
            control={control}
          />
          <Controller
            name="maxNotSeenToClose"
            defaultValue={
              maxDaysNotSeenToCloseSetting
                ? maxDaysNotSeenToCloseSetting
                : DEFAULT_MAX_DAYS_NOT_REPORTED_TO_CLOSE_FINDING
            }
            render={({ field: { value, onChange, onBlur, ref } }) => {
              return (
                <TextInput
                  data-testid={"max-not-seen-to-close"}
                  onChange={(value) => {
                    onChange(value);
                  }}
                  inputRef={ref}
                  name={"maxNotSeenToClose"}
                  onBlur={onBlur}
                  value={value}
                  error={errors?.maxNotSeenToClose}
                  min={1}
                  disabled={!enableLastReportedThreshold || disabled}
                  type={"number"}
                  className="max-not-seen-to-close"
                  placeholder={"Enter Days..."}
                  required
                />
              );
            }}
            control={control}
            rules={
              enableLastReportedThreshold
                ? {
                    required: REQUIRED_ERROR_MESSAGE,
                    pattern: {
                      value: /^[1-9][0-9]*$/,
                      message: "Invalid number of days",
                    },
                    max: {
                      value: MAX_DAYS_TO_CLOSE_FINDING,
                      message: t(
                        `Enter a value from 1 to {{MAX_DAYS_TO_CLOSE_FINDING}} days`,
                        { MAX_DAYS_TO_CLOSE_FINDING }
                      ),
                    },
                    min: {
                      value: 1,
                      message: t(
                        `Enter a value from 1 to {{MAX_DAYS_TO_CLOSE_FINDING}} days`,
                        { MAX_DAYS_TO_CLOSE_FINDING }
                      ),
                    },
                  }
                : { required: false, min: 1 }
            }
          />
          <div className="days-ago">&nbsp; {t("days ago")} </div>
        </div>
        <div className="last-collected-threshold-toggle-and-value checkbox-and-number">
          <Controller
            name={`enableLastCollectedThreshold`}
            defaultValue={enableLastCollectedThreshold}
            render={({ field: { value, onChange } }) => {
              return (
                <CheckBox
                  className="last-collected-threshold-checkbox"
                  data-testid={"enable-last-collected-threshold"}
                  onChange={(val, newValue) => {
                    onChange(newValue);
                  }}
                  disabled={!shouldEnableLastCollected || disabled}
                  checked={value}
                  label={
                    <span>
                      {t("Automatically close any findings where the")}{" "}
                      <span className="semibold">
                        {t("Last Collected Time")}
                      </span>{" "}
                      {t("is more than")}
                    </span>
                  }
                />
              );
            }}
            control={control}
          />
          <Controller
            name="lastCollectedThreshold"
            defaultValue={
              lastCollectedThreshold
                ? lastCollectedThreshold
                : DEFAULT_MAX_DAYS_NOT_COLLECTED_TO_CLOSE_FINDING
            }
            render={({ field: { value, onChange, onBlur, ref } }) => {
              return (
                <TextInput
                  data-testid={"max-not-collected-to-close"}
                  onChange={(value) => {
                    onChange(value);
                  }}
                  inputRef={ref}
                  name={"lastCollectedThreshold"}
                  onBlur={onBlur}
                  value={value}
                  error={errors?.lastCollectedThreshold}
                  min={1}
                  disabled={!enableLastCollectedThreshold || disabled}
                  type={"number"}
                  className="last-collected-threshold-input"
                  placeholder={"Enter Days..."}
                  required
                />
              );
            }}
            control={control}
            rules={
              enableLastCollectedThreshold
                ? {
                    required: REQUIRED_ERROR_MESSAGE,
                    pattern: {
                      value: /^[1-9][0-9]*$/,
                      message: t(
                        `Enter a value from 1 to {{MAX_DAYS_TO_CLOSE_FINDING}} days`,
                        { MAX_DAYS_TO_CLOSE_FINDING }
                      ),
                    },
                    max: {
                      value: enableLastReportedThreshold
                        ? Math.min(MAX_DAYS_TO_CLOSE_FINDING, maxNotSeenToClose)
                        : MAX_DAYS_TO_CLOSE_FINDING,
                      message: enableLastReportedThreshold
                        ? maxNotSeenToClose < lastCollectedThresholdFv
                          ? "last reported threshold should be greater than last collected threshold"
                          : `Enter a value from 1 to ${MAX_DAYS_TO_CLOSE_FINDING} days`
                        : `Enter a value from 1 to ${MAX_DAYS_TO_CLOSE_FINDING} days`,
                    },
                  }
                : { required: false, max: 1000000 }
            }
          />
          <div className="days-ago">&nbsp; {t("days ago")} </div>
          <Tooltip
            className="collected-threshold-tooltip"
            type="info"
            content={`Last Collected Time value must be =/< Last Reported Time value`}
          >
            <i className="collected-threshold-help seem-icon seem-icon-help" />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
export default PeriodicSettings;
